import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { getToken, triggerLogout } from '@/lib/auth';
import { Sentry } from '@/lib/sentry';

const httpLink = new HttpLink({
    uri: import.meta.env.VITE_GRAPHQL_URL || 'http://localhost:3000/graphql',
});

const authLink = new SetContextLink(({ headers }) => {
    const token = getToken();
    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
        }
    }
});

const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
        const hasAuthError = error.errors.some(
            (err) => err.extensions?.code === 'UNAUTHORIZED'
        );
        if (hasAuthError) {
            triggerLogout();
            return;
        }

        const hasServerError = error.errors.some(
            (err) => err.extensions?.code === 'INTERNAL' || err.extensions?.statusCode === 500
        );
        if (hasServerError) {
            Sentry.captureException(error, {
                tags: { type: 'graphql' },
                contexts: {
                    graphql: {
                        errors: error.errors.map((e) => ({
                            message: e.message,
                            code: e.extensions?.code,
                            path: e.path?.join('.'),
                        })),
                    },
                },
            });
        }
    }

    if ('networkError' in error && error.networkError) {
        Sentry.captureException(error.networkError, {
            tags: { type: 'network' },
        });
    }
});

export const client = new ApolloClient({
    link: errorLink.concat(authLink).concat(httpLink),
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    listRaces: {
                        keyArgs: ['filter'],
                        merge(_, incoming) { return incoming; },
                    },
                },
            },
        },
    }),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
            nextFetchPolicy: 'cache-first',
        },
    },
});
